import { registerLocaleData } from '@angular/common';
import ptBr from '@angular/common/locales/pt';
import { Component, LOCALE_ID, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HeaderTitle } from '../../models/generic';
import { ServiceCode, ServiceCodeForm, ServiceCodeRateKey } from '../../models/service-code';
import { SnackbarType } from '../../models/snackbar-type.enum';
import { CodigosServicoService } from '../../services/codigos-servico.service';
import { DataService } from '../../services/data.service';
import { SnackbarService } from '../../services/snackbar.service';

interface RateColumn {
  key: ServiceCodeRateKey;
  label: string;
}

@Component({
  selector: 'app-codigos-servico',
  templateUrl: './codigos-servico.component.html',
  styleUrls: ['./codigos-servico.component.scss'],
  providers: [
    { provide: LOCALE_ID, useValue: 'pt-BR' }
  ]
})
export class CodigosServicoComponent implements OnInit {
  // Mesma ordem das colunas do protótipo (tabela e modal).
  readonly rateColumns: RateColumn[] = [
    { key: 'pisRate', label: 'PIS' },
    { key: 'csllRate', label: 'CSLL' },
    { key: 'issRate', label: 'ISS' },
    { key: 'cofinsRate', label: 'COFINS' },
    { key: 'irRate', label: 'IR' },
    { key: 'inssRate', label: 'INSS' }
  ];

  // O catálogo é pequeno (itens da LC 116) e a tela não pagina: carrega tudo uma vez e filtra no cliente.
  private readonly pageSize = 1000;

  searchTerm = '';

  selectedItem: ServiceCode | null = null;
  showDeleteModal = false;
  showEditModal = false;

  form: ServiceCodeForm = this.emptyForm();
  formErrors: { [key: string]: string } = {};
  modalErrorMessage = '';

  allItems: ServiceCode[] = [];

  constructor(
    private activatedRoute: ActivatedRoute,
    private dataService: DataService,
    private codigosServicoService: CodigosServicoService,
    private snackbarService: SnackbarService,
  ) {
    registerLocaleData(ptBr);
    const title = activatedRoute.snapshot.routeConfig?.title as HeaderTitle;
    this.dataService.setHeaderTitle(title);
  }

  ngOnInit(): void {
    this.loadItems();
  }

  loadItems(): void {
    const params = { search: '', sortColumn: '', sortDirection: '', page: 1, rowsPerPage: this.pageSize };

    this.codigosServicoService.getAll(params).subscribe({
      next: response => {
        this.allItems = response.items;
        this.updateSubheader();
      },
      error: err => this.showError(err, 'Erro ao carregar códigos de serviço.')
    });
  }

  get items(): ServiceCode[] {
    const term = this.normalize(this.searchTerm);

    if (!term) {
      return this.allItems;
    }

    return this.allItems.filter(item =>
      this.normalize(item.code).includes(term) || this.normalize(item.description).includes(term));
  }

  get formTotalRate(): number {
    return this.rateColumns.reduce((sum, col) => sum + this.parseRate(this.form.rates[col.key]), 0);
  }

  onSearchChange(term: string): void {
    this.searchTerm = term;
  }

  // Totalizadores no subheader global (sobre o catálogo inteiro, não sobre o filtro da busca).
  // O override e o resumo precisam ser definidos depois da navegação: o subheader os limpa a cada NavigationEnd.
  private updateSubheader(): void {
    this.dataService.setSubheaderOverride({
      title: 'Códigos de Serviço',
      plainTitle: true
    });

    this.dataService.setHeaderSummary([
      { label: 'Códigos cadastrados', value: this.allItems.length },
      { label: 'Ativos', value: this.allItems.filter(item => item.isEnabled).length },
      { label: 'Com retenção', value: this.allItems.filter(item => this.hasRate(item.totalRate)).length }
    ]);
  }

  private normalize(value: string | null | undefined): string {
    return (value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  }

  openCreateModal(): void {
    this.selectedItem = null;
    this.form = this.emptyForm();
    this.formErrors = {};
    this.modalErrorMessage = '';
    this.showEditModal = true;
  }

  openEditModal(item: ServiceCode): void {
    this.selectedItem = item;
    this.form = {
      id: item.serviceCodeId,
      code: item.code,
      description: item.description,
      isEnabled: item.isEnabled,
      rates: {
        issRate: this.formatRate(item.issRate),
        irRate: this.formatRate(item.irRate),
        pisRate: this.formatRate(item.pisRate),
        cofinsRate: this.formatRate(item.cofinsRate),
        csllRate: this.formatRate(item.csllRate),
        inssRate: this.formatRate(item.inssRate)
      }
    };
    this.formErrors = {};
    this.modalErrorMessage = '';
    this.showEditModal = true;
  }

  saveItem(): void {
    this.modalErrorMessage = '';

    if (!this.validateForm()) {
      return;
    }

    const { id, code, description, isEnabled, rates } = this.form;

    const payload = {
      code: code.trim(),
      description: description.trim(),
      isEnabled,
      issRate: this.parseRate(rates.issRate),
      irRate: this.parseRate(rates.irRate),
      pisRate: this.parseRate(rates.pisRate),
      cofinsRate: this.parseRate(rates.cofinsRate),
      csllRate: this.parseRate(rates.csllRate),
      inssRate: this.parseRate(rates.inssRate)
    };

    const request = id
      ? this.codigosServicoService.update(id, payload)
      : this.codigosServicoService.create(payload);

    request.subscribe({
      next: () => {
        this.loadItems();
        this.closeEditModal();
        this.snackbarService.openSnackBar('Código de serviço salvo com sucesso!', SnackbarType.Success, 3000);
      },
      error: err => this.showError(err, 'Erro ao salvar código de serviço.')
    });
  }

  confirmRemove(item: ServiceCode): void {
    this.selectedItem = item;
    this.showDeleteModal = true;
  }

  executeDelete(): void {
    if (this.selectedItem) {
      this.codigosServicoService.delete(this.selectedItem.serviceCodeId).subscribe({
        next: () => {
          this.loadItems();
          this.snackbarService.openSnackBar('Código de serviço excluído com sucesso!', SnackbarType.Success, 3000);
        },
        error: err => this.showError(err, 'Erro ao excluir código de serviço.')
      });
    }
    this.closeDeleteModal();
  }

  closeEditModal(): void {
    this.showEditModal = false;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
  }

  hasRate(value: number | null | undefined): boolean {
    return !!value && value > 0;
  }

  validateForm(): boolean {
    this.formErrors = {};

    if (!this.form.code.trim()) {
      this.formErrors['code'] = 'O Código do Serviço é obrigatório.';
    } else if (this.form.code.trim().length > 20) {
      this.formErrors['code'] = 'O Código do Serviço deve ter no máximo 20 caracteres.';
    }

    if (!this.form.description.trim()) {
      this.formErrors['description'] = 'A Descrição do Serviço é obrigatória.';
    } else if (this.form.description.trim().length > 500) {
      this.formErrors['description'] = 'A Descrição do Serviço deve ter no máximo 500 caracteres.';
    }

    // Espelha a regra do backend: acima de 100% o líquido do sublote ficaria negativo.
    if (this.formTotalRate > 100) {
      this.formErrors['rates'] = 'A soma das alíquotas não pode ultrapassar 100%.';
    }

    if (Object.keys(this.formErrors).length > 0) {
      this.showError(null, 'Por favor, preencha todos os campos corretamente.');
      return false;
    }

    return true;
  }

  // O PercentMaskDirective entrega o valor como texto ("1,50 %"); a API trabalha com número.
  private parseRate(value: string | null | undefined): number {
    if (!value) return 0;
    const parsed = parseFloat(value.replace(/[^0-9,]/g, '').replace(',', '.'));
    return isNaN(parsed) ? 0 : parsed;
  }

  private formatRate(value: number | null | undefined): string {
    return this.hasRate(value) ? (value as number).toFixed(2).replace('.', ',') : '';
  }

  private emptyForm(): ServiceCodeForm {
    return {
      code: '',
      description: '',
      isEnabled: true,
      rates: { issRate: '', irRate: '', pisRate: '', cofinsRate: '', csllRate: '', inssRate: '' }
    };
  }

  private showError(err: any, fallback: string): void {
    const message = err?.error?.Message || err?.error?.message || fallback;
    this.modalErrorMessage = message;
    this.snackbarService.openSnackBar(message, SnackbarType.Danger, 5000);

    setTimeout(function () {
      document.querySelector('.loader')?.remove();
    }, 1000);
  }
}
